import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Skeleton, Switch, TimePicker, Tooltip } from 'antd';
import { ArrowLeftOutlined, BellOutlined, CheckOutlined, LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { Button, Chip, PageTitle, Panel } from '../components/ds';
import { authApi } from '../lib/authApi';
import { notificationsApi } from '../lib/wikiApi';
import { NOTIFY_EVENTS, notifPrefsApi, type NotifyChannels, type NotifyEvent } from '../lib/api/admin';
import { disablePush, enablePush, getPushState, type PushState } from '../lib/push';
import './analytics/analytics.css';

/** События матрицы — те же ключи, что на бэке (notifyEventOf в wiki-notifications.service). */
const EVENTS: Record<NotifyEvent, [string, string]> = {
  courses: ['Обучение', 'назначенные курсы, траектории, сроки, переаттестация, вопрос дня'],
  reviews: ['Проверка ответов', 'тест или практика проверены, возвращены на доработку'],
  standards: ['Стандарты', 'новые и обновлённые стандарты для подтверждения'],
  verify: ['Статьи на проверке', 'согласование статей и напоминания владельцам'],
  questions: ['Вопросы и обращения', 'ответы УК на ваши вопросы'],
  mentions: ['Упоминания', 'вас упомянули в комментарии'],
  opening: ['Открытие точки', 'проверка помещения и шаги открытия'],
  other: ['Прочее', 'остальные уведомления HUB'],
};

/**
 * Настройки уведомлений HUB 2.0 (/notifications): матрица «событие × канал»
 * — колокол HUB всегда включён (важное не теряется), push выключается по
 * событию (wiki_user_prefs.channels, бэк учитывает при отправке). Telegram и
 * e-mail для вики пока не отправляются — в матрице их нет. Ниже — push на
 * этом устройстве, подписки на разделы базы, тихие часы, режим доставки.
 * Сотруднику iiko доступен только push на устройстве (персональных настроек нет).
 */
export function NotificationSettingsPage(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['wiki', 'notifications', 'prefs'], queryFn: () => notifPrefsApi.get(), retry: false });
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ['wiki', 'notifications', 'prefs'] });

  const [pushState, setPushState] = useState<PushState>('unsubscribed');
  const [pushBusy, setPushBusy] = useState(false);
  const [channels, setChannels] = useState<NotifyChannels | null>(null);
  useEffect(() => {
    void getPushState().then(setPushState);
  }, []);
  useEffect(() => {
    if (data) setChannels(data.channels ?? {});
  }, [data]);

  const blockMutation = useMutation({
    mutationFn: ({ blockId, enabled }: { blockId: string; enabled: boolean }) => notificationsApi.setBlock(blockId, enabled),
    onSuccess: invalidate,
    onError: (e: Error) => void message.error(e.message),
  });
  const prefsMutation = useMutation({
    mutationFn: (patch: { quietHoursStart?: string | null; quietHoursEnd?: string | null; digestMode?: string }) => notificationsApi.setPrefs(patch),
    onSuccess: () => {
      void message.success('Сохранено');
      invalidate();
    },
    onError: (e: Error) => void message.error(e.message),
  });
  const channelsMutation = useMutation({
    mutationFn: (ch: NotifyChannels) => notifPrefsApi.setChannels(ch),
    onSuccess: () => {
      void message.success('Каналы сохранены');
      invalidate();
    },
    onError: (e: Error) => void message.error(e.message),
  });

  const togglePush = async (): Promise<void> => {
    setPushBusy(true);
    try {
      const next = pushState === 'subscribed' ? await disablePush() : await enablePush();
      setPushState(next);
      if (next === 'subscribed') void message.success('Push-уведомления включены');
      else if (next === 'denied') void message.warning('Уведомления запрещены в браузере');
      else if (next === 'unsupported') void message.info('Браузер не поддерживает push (или открыто не на wiki.brasol.ru)');
    } catch (e) {
      void message.error((e as Error).message);
    } finally {
      setPushBusy(false);
    }
  };

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) return <Alert type="error" showIcon message="Не удалось загрузить настройки" description={(error as Error).message} />;
  if (!data) return <></>;

  const isEmployee = me.data?.type === 'iiko_employee';
  const ch = channels ?? {};
  const dirty = JSON.stringify(ch) !== JSON.stringify(data.channels ?? {});
  const pushOf = (ev: NotifyEvent): boolean => ch[ev]?.push !== false;

  return (
    <div className="fk-adm-page" style={{ maxWidth: 920 }}>
      <PageTitle
        crumbs={
          <Link to="/profile" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeftOutlined /> Профиль
          </Link>
        }
        title="Уведомления"
        sub="Что и куда присылать. Колокол в HUB получает всё — важные назначения и сроки обучения не теряются."
        style={{ marginBottom: 0 }}
      />

      {!data.globalEnabled && (
        <Alert
          type="info"
          showIcon
          message="Рассылка обновлений базы пока выключена"
          description="УК ещё не включила рассылку обновлений статей. Ваши настройки сохранятся и заработают, когда включат."
        />
      )}

      <Panel
        title="Push на этом устройстве"
        icon={<BellOutlined />}
        right={
          <Button
            size="sm"
            variant={pushState === 'subscribed' ? 'ghost' : 'primary'}
            icon={<BellOutlined />}
            loading={pushBusy}
            disabled={pushState === 'unsupported' || pushState === 'denied'}
            onClick={() => void togglePush()}
          >
            {pushState === 'subscribed' ? 'Отключить' : 'Включить'}
          </Button>
        }
      >
        <div className="fk-adm-note" style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
          {pushState === 'subscribed'
            ? 'Включено — уведомления приходят на это устройство даже при закрытой вкладке.'
            : 'Мгновенные уведомления о курсах, проверке и новых стандартах.'}
          {pushState === 'denied' && <div style={{ color: 'var(--fk-error)', marginTop: 4 }}>Уведомления запрещены — разрешите их в настройках браузера для wiki.brasol.ru.</div>}
          {pushState === 'unsupported' && <div style={{ marginTop: 4 }}>Недоступно в этом браузере.</div>}
        </div>
      </Panel>

      {!isEmployee && (
        <Panel
          title="События и каналы"
          sub="push выключается по событию; Telegram и e-mail для HUB пока не отправляются"
          pad={0}
          right={
            <Button size="sm" variant="primary" disabled={!dirty} loading={channelsMutation.isPending} onClick={() => channelsMutation.mutate(ch)}>
              Сохранить
            </Button>
          }
        >
          <div style={{ overflowX: 'auto' }}>
            <table className="fk-adm-matrix">
              <thead>
                <tr>
                  <th>Событие</th>
                  <th className="fk-adm-c">HUB</th>
                  <th className="fk-adm-c">Push</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFY_EVENTS.map((ev) => (
                  <tr key={ev}>
                    <td>
                      <b>{EVENTS[ev][0]}</b>
                      <div className="fk-adm-note">{EVENTS[ev][1]}</div>
                    </td>
                    <td className="fk-adm-c">
                      <Tooltip title="Колокол HUB получает всё">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--fk-success-text)' }}>
                          <CheckOutlined />
                          <LockOutlined style={{ fontSize: 11, color: 'var(--fk-text-muted)' }} />
                        </span>
                      </Tooltip>
                    </td>
                    <td className="fk-adm-c">
                      <Switch size="small" checked={pushOf(ev)} onChange={(v) => setChannels({ ...ch, [ev]: { push: v } })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {data.blocks.length > 0 && (
        <Panel title="Подписки на разделы базы" sub="уведомлять о новых и изменённых статьях">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.blocks.map((b) => (
              <Chip key={b.blockId} active={b.enabled} onClick={() => blockMutation.mutate({ blockId: b.blockId, enabled: !b.enabled })}>
                {b.enabled && <CheckOutlined />}
                {b.name}
              </Chip>
            ))}
          </div>
        </Panel>
      )}

      {!isEmployee && (
        <div className="fk-adm-grid2 fk-adm-grid2--even">
          <Panel title="Тихие часы" sub="по вашему времени; в этот промежуток уведомления откладываются">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <TimePicker
                format="HH:mm"
                minuteStep={30}
                placeholder="с"
                value={data.quietHoursStart ? dayjs(data.quietHoursStart, 'HH:mm') : null}
                onChange={(t) => prefsMutation.mutate({ quietHoursStart: t ? t.format('HH:mm') : null })}
              />
              <span>—</span>
              <TimePicker
                format="HH:mm"
                minuteStep={30}
                placeholder="до"
                value={data.quietHoursEnd ? dayjs(data.quietHoursEnd, 'HH:mm') : null}
                onChange={(t) => prefsMutation.mutate({ quietHoursEnd: t ? t.format('HH:mm') : null })}
              />
            </div>
          </Panel>
          <Panel title="Режим доставки">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Chip active={data.digestMode !== 'weekly'} onClick={() => prefsMutation.mutate({ digestMode: 'instant' })}>
                Сразу по событию
              </Chip>
              <Chip active={data.digestMode === 'weekly'} onClick={() => prefsMutation.mutate({ digestMode: 'weekly' })}>
                Дайджест по понедельникам
              </Chip>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
